import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { X, HelpCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface WarmupSpeedDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedMailboxes: any[];
  onApplySpeed: (speed: string) => void;
}

const speedOptions = [
  {
    id: 'tortoise',
    name: 'Tortoise',
    speed: 'slow',
    description: 'Gradually increase email sending with maximum safety.',
    duration: '~45 days',
    emoji: '🐢',
    details: 'The safest approach to warm up your mailbox. Recommended for new domains and strict email providers.'
  },
  {
    id: 'rabbit',
    name: 'Rabbit',
    speed: 'medium',
    description: 'Balanced approach to warm up your mailbox.',
    duration: '~30 days',
    emoji: '🐰',
    details: 'A balanced approach that provides good safety while reaching your target volume efficiently.'
  },
  {
    id: 'leopard',
    name: 'Leopard',
    speed: 'fast',
    description: 'Quickly reach your target sending volume.',
    duration: '~15 days',
    emoji: '🐆',
    details: 'The fastest approach to reach your target volume. Use with caution on new domains.'
  }
];

export function WarmupSpeedDialog({
  open,
  onOpenChange,
  selectedMailboxes,
  onApplySpeed,
}: WarmupSpeedDialogProps) {
  const [selectedSpeed, setSelectedSpeed] = useState<string>('tortoise');

  const handleApply = () => {
    const speedOption = speedOptions.find(option => option.id === selectedSpeed);
    if (speedOption) {
      onApplySpeed(speedOption.name);
      onOpenChange(false);
    }
  };

  const selectedOption = speedOptions.find(option => option.id === selectedSpeed);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader className="flex flex-row items-center justify-between pb-2">
          <DialogTitle className="text-lg">Settings</DialogTitle>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onOpenChange(false)}
            className="h-5 w-5"
          >
            <X className="h-3 w-3" />
          </Button>
        </DialogHeader>

        <div className="space-y-4">
          {/* Selected Mailboxes Info */}
          <div className="bg-muted/50 p-2 rounded">
            <p className="text-xs text-muted-foreground">
              Setting warmup speed for <span className="font-medium text-foreground">{selectedMailboxes.length}</span> selected mailbox{selectedMailboxes.length !== 1 ? 'es' : ''}
            </p>
          </div>

          {/* Speed Selection Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium text-blue-600">Speed of increasing temperature</h3>
              <HelpCircle className="h-3 w-3 text-blue-600" />
            </div>

            {/* Speed Options in One Row */}
            <div className="grid grid-cols-3 gap-2">
              {speedOptions.map((option) => (
                <div
                  key={option.id}
                  onClick={() => setSelectedSpeed(option.id)}
                  className={cn(
                    "flex flex-col items-center p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 text-center",
                    selectedSpeed === option.id
                      ? "border-blue-500 bg-blue-50/10"
                      : "border-border hover:border-blue-300 hover:bg-muted/50"
                  )}
                >
                  <div className="text-xl mb-1">{option.emoji}</div>
                  <h4 className="text-sm font-semibold mb-1">{option.name}</h4>
                  <span className="text-xs text-muted-foreground mb-1">({option.speed})</span>
                  <p className="text-xs text-muted-foreground mb-2 leading-tight">{option.description}</p>
                  <span className="text-xs font-medium text-blue-600">{option.duration}</span>
                </div>
              ))}
            </div>

            {/* Selected Speed Details */}
            {selectedOption && (
              <div className="bg-muted/30 p-2 rounded">
                <h4 className="text-xs font-medium text-center mb-1">{selectedOption.speed} mode:</h4>
                <p className="text-xs text-muted-foreground text-center leading-tight">
                  {selectedOption.details}
                </p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              size="sm"
              className="h-7 px-3 text-xs"
            >
              Cancel
            </Button>
            <Button
              onClick={handleApply}
              size="sm"
              className="h-7 px-3 text-xs bg-blue-600 hover:bg-blue-700"
            >
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
